import React, {Component} from 'react';
import {Input} from "reactstrap";
import {withTranslation} from 'react-i18next';
import {ITEM_ALL_CONVERSIONS} from "./ActionsMultiSelect";
import {FACEBOOK_CHANNEL} from "../channels/channels";
import {LinkToArticle, STYLE_WANT_TO_KNOW_MORE} from "../help_center/LinkToArticle";
import {ownEventsInfoFor} from "./channelOwnEvents";
import {
    CATEGORY_POPULAR,
    CATEGORY_ORDER,
    CATEGORY_I18N_KEYS,
    CATEGORY_FILTERS,
    countContainedActions,
    groupActionsByCategory,
    isPopularAction,
    getCategoryForAction,
    overlapsWithSelected,
    stripActionPrefix,
    getTranslatedName,
    getHelpTooltip,
} from "./actionCategories";

// Cuántos eventos con datos se ofrecen cuando la búsqueda no encontró nada. Con más, el bloque
// deja de ser una ayuda y se vuelve una segunda lista.
const MAX_SUGGESTED_WITH_DATA = 4;


class CategorizedActionsSelectClass extends Component {
    constructor(props) {
        super(props);

        const selectedIds = this._getInitialSelectedIds();
        const expandedCategories = this._getInitialExpandedCategories(selectedIds);

        this.state = {
            selectedIds,
            expandedCategories,
            searchText: '',
            activeFilterId: null,
            // El filtro "Con datos" arranca prendido SÓLO cuando no hay nada elegido todavía: ahí
            // el usuario está descubriendo, y de 117 renglones le interesan los que su cuenta
            // registra de verdad. Con una selección ya hecha, prenderlo escondería lo que eligió.
            onlyWithData: this._shouldStartFilteredByData(),
            // Aviso de solapamiento: `{containerId, containedId}` del último tilde que duplica.
            overlapNotice: null,
        };

        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleItemChange = this.handleItemChange.bind(this);
        this.handleAllConversionsChange = this.handleAllConversionsChange.bind(this);
        this.handleToggleOnlyWithData = this.handleToggleOnlyWithData.bind(this);
        this.handleSelectAggregate = this.handleSelectAggregate.bind(this);
    }

    // --- Inicialización ---

    _getInitialSelectedIds() {
        const ids = (this.props.selectedItemIds || []).map(id => id.toString());
        if (this._isControlled()) {
            // Modo controlado: el padre decide explícitamente si "Todas las primarias" está activa.
            // Permite tri-estado: ningún evento (false) / todas las primarias (true) / eventos específicos.
            if (this.props.useAllPrimaryConversions === true) {
                return [ITEM_ALL_CONVERSIONS.id.toString()];
            }
            return ids;
        }
        if (ids.length === 0 && this.props.hasAllPrimaryConversionsOption) {
            return [ITEM_ALL_CONVERSIONS.id.toString()];
        }
        return ids;
    }

    _isControlled() {
        return this.props.useAllPrimaryConversions !== undefined;
    }

    _shouldStartFilteredByData() {
        const items = this.props.items || [];
        const withData = items.filter(item => this._itemHasData({raw: item})).length;
        // Si la cuenta no registró nada, o si TODO tiene datos, el filtro no recorta: prenderlo
        // sólo agregaría una píldora encendida que no cambia la lista.
        //
        // La primera versión además lo apagaba cuando el usuario YA tenía eventos elegidos, para
        // no esconderle su selección. Era innecesario —`_itemPassesFilter` nunca oculta un item
        // seleccionado— y tenía un costo alto: la cuenta típica ya tiene algo elegido, así que en
        // la práctica el filtro casi nunca arrancaba puesto y la primera pantalla seguía siendo la
        // lista larga con ceros, que es justo lo que este frente venía a evitar (QA 01).
        return withData > 0 && withData < items.length;
    }

    _getInitialExpandedCategories(selectedIds) {
        if (!this._shouldCategorize()) return {};

        const items = this._getMappedItems();
        const expanded = {};

        CATEGORY_ORDER.forEach(cat => {
            expanded[cat] = false;
        });
        expanded[CATEGORY_POPULAR] = true; // Populares siempre expandido por defecto
        // El grupo de eventos propios arranca CERRADO cuando tiene contenido (es una lista más),
        // y ABIERTO cuando está vacío: ahí adentro no hay una lista sino el instructivo de cómo
        // conseguir que el evento aparezca, y detrás de un click es lo mismo que no estar.
        expanded['custom'] = items.every(item => !item.raw.is_custom);

        const info = this._ownEventsInfo();
        const idAgregado = info && info.aggregateGroupKey ? info.aggregateActionId : null;
        // El cajón del renglón agregado nace cerrado: adentro hay UN renglón y su explicación, no
        // una lista que valga la pena tener abierta de entrada.
        expanded['aggregate'] = false;

        // Expandir categorías que tienen items seleccionados
        items.forEach(item => {
            if (selectedIds.includes(item.id)) {
                const bare = stripActionPrefix(item.raw.action_id);
                const category = bare === idAgregado ? 'aggregate'
                    : (item.raw.is_custom ? 'custom' : (getCategoryForAction(item.raw.action_id) || 'other'));
                expanded[category] = true;
            }
        });

        // Si ninguna categoría está expandida, expandir la primera que tenga items
        const anyExpanded = CATEGORY_ORDER.some(cat => expanded[cat]);
        if (!anyExpanded) {
            const {categorized} = this._getGroupedItems();
            for (const cat of CATEGORY_ORDER) {
                if (categorized[cat] && categorized[cat].length > 0) {
                    expanded[cat] = true;
                    break;
                }
            }
        }

        return expanded;
    }

    _getMappedItems() {
        const itemDataMap = this.props.itemDataMap || ((item) => ({
            name: item.name,
            id: item.id != null ? item.id.toString() : null,
        }));
        return (this.props.items || []).map(rawItem => ({
            ...itemDataMap(rawItem),
            raw: rawItem,
        }));
    }

    _shouldCategorize() {
        return this.props.channel === FACEBOOK_CHANNEL;
    }

    _ownEventsInfo() {
        return ownEventsInfoFor(this.props.channel);
    }

    _getGroupedItems() {
        const itemDataMap = this.props.itemDataMap || ((item) => ({
            name: item.name,
            id: item.id != null ? item.id.toString() : null,
        }));
        const grupos = groupActionsByCategory(this.props.items || [], itemDataMap);

        // El renglón agregado del canal sale de las categorías: tiene su propio grupo al final,
        // junto a la explicación de por qué la plataforma no desglosa esos eventos. Antes vivía
        // en DOS lugares a la vez (Populares y Otros) y la explicación en un tercero, suelta al
        // pie de la pantalla.
        const info = this._ownEventsInfo();
        if (info && info.aggregateGroupKey && info.aggregateActionId) {
            const esElAgregado = (item) =>
                stripActionPrefix(item.raw.action_id) === info.aggregateActionId;
            CATEGORY_ORDER.forEach(cat => {
                grupos.categorized[cat] = (grupos.categorized[cat] || []).filter(i => !esElAgregado(i));
            });
        }
        return grupos;
    }

    /** El renglón del canal que suma lo que la plataforma no desglosa, si esta cuenta lo trae. */
    _aggregateGroupItems() {
        const info = this._ownEventsInfo();
        if (!info || !info.aggregateGroupKey || !info.aggregateActionId) return [];
        return this._getMappedItems()
            .filter(item => stripActionPrefix(item.raw.action_id) === info.aggregateActionId);
    }

    _isAllConversionsSelected() {
        return this.state.selectedIds.includes(ITEM_ALL_CONVERSIONS.id.toString());
    }

    // --- Datos de la cuenta ---

    /**
     * CONTRATO con el backend, y la parte que importa es la ausencia:
     *   `counts` presente con ceros ⇒ lo medimos y no pasó nada
     *   `counts` ausente            ⇒ este canal no informa conteos
     * Por eso `_itemHasData` pregunta por los valores y `_itemMeasured` por la clave: son dos
     * preguntas distintas y antes se veían iguales en pantalla (las dos, en blanco).
     */
    _itemHasData(item) {
        const counts = item.raw && item.raw.counts;
        if (!counts) return false;
        return !!(counts.yesterday || counts.last_7_days || counts.last_30_days);
    }

    _itemMeasured(item) {
        return !!(item.raw && item.raw.counts);
    }

    _itemsWithData() {
        return this._getMappedItems().filter(item => this._itemHasData(item));
    }

    _itemsWithDataSorted() {
        return this._itemsWithData().sort((a, b) =>
            (b.raw.counts.last_30_days || 0) - (a.raw.counts.last_30_days || 0));
    }

    // --- Handlers ---

    handleSearchChange(event) {
        this.setState({searchText: event.target.value});
    }

    handleToggleOnlyWithData() {
        this.setState(prevState => ({onlyWithData: !prevState.onlyWithData}));
    }

    handleItemChange(event) {
        const isChecked = event.target.checked;
        const itemId = event.target.dataset.id;
        this._changeSelection(itemId, isChecked);
    }

    _changeSelection(itemId, isChecked) {
        let selectedIds;
        let overlapNotice = null;

        if (isChecked) {
            selectedIds = this.state.selectedIds
                .filter(id => id !== ITEM_ALL_CONVERSIONS.id.toString())
                .concat([itemId]);
            // El solapamiento se avisa al TILDAR, no al guardar: es el momento en que el usuario
            // todavía tiene las dos opciones en la cabeza y puede elegir cuál deja.
            overlapNotice = overlapsWithSelected(itemId, selectedIds)[0] || null;
        } else {
            selectedIds = this.state.selectedIds.filter(id => id !== itemId);
            // En modo legacy, deseleccionar el último evento auto-revierte a "Todas las primarias".
            // En modo controlado el padre permite explícitamente dejar 0 eventos.
            if (selectedIds.length === 0 && this.props.hasAllPrimaryConversionsOption && !this._isControlled()) {
                selectedIds = [ITEM_ALL_CONVERSIONS.id.toString()];
            }
        }

        this.setState({overlapNotice});
        this._setSelectedIds(selectedIds);
    }

    /** Tilda el renglón agregado del canal desde el estado vacío, y limpia la búsqueda. */
    handleSelectAggregate() {
        const item = this._aggregateItem();
        if (!item) return;
        this.setState({searchText: ''});
        this._changeSelection(item.id, true);
    }

    _aggregateItem() {
        const info = this._ownEventsInfo();
        if (!info || !info.aggregateActionId) return null;
        return this._getMappedItems()
            .find(item => stripActionPrefix(item.raw.action_id) === info.aggregateActionId) || null;
    }

    handleAllConversionsChange(event) {
        const isChecked = event.target.checked;
        if (isChecked) {
            this._setSelectedIds([ITEM_ALL_CONVERSIONS.id.toString()]);
        } else if (this._isControlled()) {
            // Modo controlado: permitir desmarcar "Todas las primarias" para tener 0 eventos.
            this._setSelectedIds([]);
        } else {
            // Legacy: no se puede desmarcar "todas las primarias" sin seleccionar algo.
            this._setSelectedIds([ITEM_ALL_CONVERSIONS.id.toString()]);
        }
    }

    handleFilterClick(filterId) {
        this.setState(prevState => ({
            activeFilterId: prevState.activeFilterId === filterId ? null : filterId,
        }));
    }

    _getActiveFilter() {
        if (!this.state.activeFilterId) return null;
        return CATEGORY_FILTERS.find(f => f.id === this.state.activeFilterId) || null;
    }

    _itemPassesFilter(item) {
        // Un evento YA ELEGIDO nunca se esconde: si un filtro lo tapara, el usuario vería su
        // selección desaparecer sin haberla tocado.
        if (this._isSelected(item.id)) return true;

        // Buscar por nombre gana sobre TODOS los filtros: si escribiste el nombre de un evento,
        // querés verlo — y si no tiene datos, saberlo es justamente la respuesta que fuiste a
        // buscar. El bypass aplicaba sólo al filtro de datos, y eso volvía mentiroso el bloque
        // que contesta la búsqueda: con una categoría prendida, escribir un evento de otra
        // categoría daba cero resultados y la pantalla decía «la plataforma no lo publica con su
        // nombre» sobre un evento que existe y que había tapado una píldora.
        if (this.state.searchText) return true;

        if (this.state.onlyWithData && !this._itemHasData(item)) return false;

        const filter = this._getActiveFilter();
        if (!filter) return true;
        const bare = stripActionPrefix(item.raw.action_id);
        return filter.filterActionIds.has(bare);
    }

    handleToggleCategory(category) {
        this.setState(prevState => ({
            expandedCategories: {
                ...prevState.expandedCategories,
                [category]: !prevState.expandedCategories[category],
            }
        }));
    }

    _setSelectedIds(selectedIds) {
        this.setState({selectedIds}, () => {
            const actionsIds = selectedIds.filter(id => id !== ITEM_ALL_CONVERSIONS.id.toString());
            const useAllPrimaryConversions = selectedIds.includes(ITEM_ALL_CONVERSIONS.id.toString());
            this.props.onChange(actionsIds, useAllPrimaryConversions);
        });
    }

    _isSelected(itemId) {
        return this.state.selectedIds.includes(itemId);
    }

    _matchesSearch(item) {
        if (!this.state.searchText) return true;
        const query = this.state.searchText.toLowerCase();
        if (item.name.toLowerCase().includes(query)) return true;
        const translated = getTranslatedName(item.raw.action_id);
        return translated ? translated.toLowerCase().includes(query) : false;
    }

    /** Los items realmente visibles, sin duplicar los que se copian a "Populares". */
    _visibleItems() {
        return this._getMappedItems()
            .filter(item => this._matchesSearch(item) && this._itemPassesFilter(item));
    }

    _countSelectedInCategory(items) {
        return items.filter(item => this._isSelected(item.id)).length;
    }

    // --- Formato de conteos ---

    _formatCount(n) {
        if (n == null || n === 0) return '0';
        n = Math.round(n);
        if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
        if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
        return n.toString();
    }

    _renderCounts(counts) {
        // Sin la clave, el canal no mide: no se dibuja nada. CON la clave y en cero, se dibujan
        // los ceros — "no pasó nada" es una respuesta, y antes se veía igual que el silencio.
        if (!counts) return null;
        const {yesterday, last_7_days, last_30_days} = counts;

        const {t} = this.props;
        const periods = [
            {label: '1d', value: yesterday, tooltip: t('categorized_actions.count_yesterday')},
            {label: '7d', value: last_7_days, tooltip: t('categorized_actions.count_last_7_days')},
            {label: '30d', value: last_30_days, tooltip: t('categorized_actions.count_last_30_days')},
        ];

        return (
            <span className="action-counts">
                {periods.map(p => (
                    <span
                        key={p.label}
                        className={`action-count-segment ${p.value ? '-has-data' : '-no-data'}`}
                        title={`${p.tooltip}: ${p.value || 0}`}
                    >
                        <span className="action-count-label">{p.label}</span>
                        <span className="action-count-value">{this._formatCount(p.value)}</span>
                    </span>
                ))}
            </span>
        );
    }

    // --- Render ---

    _renderSearch() {
        const {t} = this.props;
        return (
            <div className="categorized-actions-search">
                <input
                    type="text"
                    placeholder={t('categorized_actions.search_placeholder')}
                    value={this.state.searchText}
                    onChange={this.handleSearchChange}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                />
            </div>
        );
    }

    _renderFilters() {
        const {t} = this.props;
        const withDataCount = this._itemsWithData().length;
        // La píldora sólo tiene sentido si recorta algo. Con `Con datos · 0` ofrecía un filtro que
        // deja la lista vacía, y encima le mostraba un cero al usuario — lo mismo que la línea de
        // cobertura evita callándose cuando no hay nada que contar.
        const showWithData = withDataCount > 0;
        const showCategories = this._shouldCategorize();
        if (!showWithData && !showCategories) return null;

        return (
            <div className="categorized-actions-presets">
                {/* El rótulo nombra categorías: en la vista plana (Google/GA4/LinkedIn/SHEET) no
                    hay ninguna, y prometía un control que no existe. */}
                <span className="presets-label">
                    {showCategories
                        ? t('categorized_actions.filter_by_category')
                        : t('categorized_actions.filter_label')}:
                </span>
                <div className="presets-pills">
                    {showWithData && (
                        <button
                            type="button"
                            className={`preset-pill -with-data ${this.state.onlyWithData ? '-active' : ''}`}
                            onClick={this.handleToggleOnlyWithData}
                            aria-pressed={this.state.onlyWithData}
                        >
                            {t('categorized_actions.filter_with_data')} · {withDataCount}
                        </button>
                    )}
                    {showCategories && CATEGORY_FILTERS.map(filter => {
                        const isActive = this.state.activeFilterId === filter.id;
                        return (
                            <button
                                key={filter.id}
                                type="button"
                                className={`preset-pill ${isActive ? '-active' : ''}`}
                                onClick={() => this.handleFilterClick(filter.id)}
                                aria-pressed={isActive}
                            >
                                {t(filter.i18nKey)}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    _renderAllConversionsOption() {
        const {t} = this.props;
        if (!this.props.hasAllPrimaryConversionsOption) return null;

        return (
            <div className="categorized-actions-all-conversions">
                <label className="multi-select-item">
                    <Input
                        type="checkbox"
                        checked={this._isAllConversionsSelected()}
                        onChange={this.handleAllConversionsChange}
                        data-id={ITEM_ALL_CONVERSIONS.id.toString()}
                    />
                    <p className="multi-select-item-name">
                        {t('actions_multi_select.all_primary_conversions')}
                    </p>
                </label>
            </div>
        );
    }

    /**
     * Aviso de doble conteo. Meta publica el mismo hecho con varias agrupaciones —una compra del
     * píxel viaja además dentro de "Compras agrupadas"—, así que tildar dos vecinos duplica el
     * número. Medido en una cuenta real: los dos renglones daban 41 y 41.
     */
    _renderOverlapNotice() {
        const {t} = this.props;
        const notice = this.state.overlapNotice;
        if (!notice) return null;

        const nameOf = (bareId) => getTranslatedName(bareId) || bareId;

        return (
            <div className="categorized-actions-overlap" role="status">
                <p className="overlap-title">
                    {t('categorized_actions.overlap_title', {
                        container: nameOf(notice.containerId),
                        contained: nameOf(notice.containedId),
                        interpolation: {escapeValue: false},
                    })}
                </p>
                <p className="overlap-detail">{t('categorized_actions.overlap_detail')}</p>
            </div>
        );
    }

    _renderItem(item, category) {
        const isPopular = category && (category === CATEGORY_POPULAR || isPopularAction(item.raw.action_id, category));
        const {t} = this.props;
        const translated = getTranslatedName(item.raw.action_id);
        const helpText = getHelpTooltip(item.raw.action_id);
        const displayName = translated || item.name;
        // Se cuentan los contenidos PRESENTES en esta cuenta, no los declarados: el badge es un
        // número que el usuario lee, y contar lo declarado lo hace mentir hacia arriba.
        const contained = countContainedActions(item.raw.action_id, this._getMappedItems());

        return (
            <li key={item.id}>
                <label className="multi-select-item">
                    <Input
                        type="checkbox"
                        checked={this._isSelected(item.id)}
                        onChange={this.handleItemChange}
                        data-id={item.id}
                        name={item.name}
                    />
                    <div className="multi-select-item-content">
                        <p className="multi-select-item-name">{displayName}</p>
                    </div>
                    {/* Sin `title=""`: el patrón de tooltips del repo es `warning-tooltip`, y acá
                        el que explica de verdad es el aviso de solapamiento, que aparece en el
                        momento en que el usuario tilda el segundo renglón de un par. */}
                    {contained > 0 && (
                        <span className="action-groups-badge">
                            {t('categorized_actions.contains_badge', {count: contained})}
                        </span>
                    )}
                    {this._renderCounts(item.raw.counts)}
                    {isPopular && <span className="popular-badge">&#9733; {t('categorized_actions.popular')}</span>}
                    {helpText && (
                        <span className="help-tooltip-container">
                            <span className="help-tooltip-icon">?</span>
                            <span className="help-tooltip-body">{helpText}</span>
                        </span>
                    )}
                </label>
            </li>
        );
    }

    _renderCategorySection(category, items) {
        const {t} = this.props;
        const isSearching = !!this.state.searchText;
        const isFiltering = !!this.state.activeFilterId;
        const filteredItems = items.filter(item =>
            this._matchesSearch(item) && this._itemPassesFilter(item)
        );

        if (filteredItems.length === 0) return null;

        const isExpanded = isSearching || isFiltering || this.state.expandedCategories[category];
        const selectedCount = this._countSelectedInCategory(items);
        const categoryName = t(CATEGORY_I18N_KEYS[category]);

        return (
            <div key={category} className="categorized-actions-category">
                <div
                    className={`category-header ${isExpanded ? '-expanded' : ''}`}
                    onClick={() => this.handleToggleCategory(category)}
                >
                    <span className="category-arrow">{isExpanded ? '▼' : '▶'}</span>
                    <span className="category-name">{categoryName}</span>
                    {selectedCount > 0 && (
                        <span className="category-count">
                            ({selectedCount} {selectedCount === 1 ? t('categorized_actions.selected_singular') : t('categorized_actions.selected_plural')})
                        </span>
                    )}
                </div>
                {isExpanded && (
                    <ul className="category-items">
                        {filteredItems.map(item => this._renderItem(item, category))}
                    </ul>
                )}
            </div>
        );
    }

    /**
     * El grupo de eventos propios del anunciante.
     *
     * Se muestra AUNQUE ESTÉ VACÍO cuando el canal permite elegirlos: vacío es exactamente el
     * estado de la cuenta que necesita el instructivo, y esconderlo ahí dejaba sin una sola
     * palabra al usuario que viene a buscar su evento.
     */
    _renderCustomSection(customItems) {
        const {t} = this.props;
        const info = this._ownEventsInfo();
        const canTeach = !!info && info.canPickOwn;

        if (customItems.length === 0 && !canTeach) return null;

        // Cuando hay un filtro de categoría activo, ocultar eventos propios
        if (this.state.activeFilterId) return null;

        const isSearching = !!this.state.searchText;
        const filteredItems = customItems.filter(item =>
            this._matchesSearch(item) && this._itemPassesFilter(item));

        const isEmpty = customItems.length === 0;
        if (filteredItems.length === 0 && !isEmpty) return null;
        // Con una búsqueda escrita, el estado vacío del grupo no aporta: quien contesta es el
        // bloque de "sin resultados", que dice lo mismo pero con el término que buscó.
        if (isEmpty && isSearching) return null;

        const isExpanded = isSearching || this.state.expandedCategories['custom'];
        const selectedCount = this._countSelectedInCategory(customItems);

        return (
            <div className="categorized-actions-category -custom">
                <div
                    className={`category-header ${isExpanded ? '-expanded' : ''}`}
                    onClick={() => this.handleToggleCategory('custom')}
                >
                    <span className="category-arrow">{isExpanded ? '▼' : '▶'}</span>
                    {/* El grupo se llama como LA PLATAFORMA llama a ese objeto — "Conversiones
                        personalizadas" en Meta, "Eventos clave" en GA4 — porque el usuario va a
                        tener que ir a crearlo allá. Se llamaba "Eventos personalizados (custom)"
                        para todos los canales, y en Meta ése es el nombre de OTRA cosa: la que
                        no se puede elegir. */}
                    <span className="category-name">
                        {info && info.objectKey
                            ? t(info.objectKey)
                            : t('categorized_actions.custom_events')}
                    </span>
                    {selectedCount > 0 && (
                        <span className="category-count">
                            ({selectedCount} {selectedCount === 1 ? t('categorized_actions.selected_singular') : t('categorized_actions.selected_plural')})
                        </span>
                    )}
                </div>
                {isExpanded && (isEmpty
                    ? this._renderCustomSectionEmpty(info)
                    : <>
                        <ul className="category-items">
                            {filteredItems.map(item => this._renderItem(item, null))}
                        </ul>
                        {/* Debajo de la lista, no arriba: primero lo que hay; el aviso es para
                            quien recorrió la lista y no encontró el suyo. */}
                        {info && info.notFoundKey && info.limitationKey
                            && this._renderOwnEventsNotice(info, info.notFoundKey)}
                    </>
                )}
            </div>
        );
    }

    _renderCustomSectionEmpty(info) {
        const {t} = this.props;
        return (
            <div className="categorized-actions-teach -inside-category">
                <p className="teach-title">{t('categorized_actions.own_events_empty_title')}</p>
                <p className="teach-detail">{t(info.howToKey)}</p>
                {info.helpArticle && (
                    <LinkToArticle article={info.helpArticle} type={STYLE_WANT_TO_KNOW_MORE}
                                   text={t('categorized_actions.own_events_how_to_link')} plain={true}/>
                )}
            </div>
        );
    }

    /**
     * Lo que la CUENTA registró de verdad, ofrecido en el lugar donde el usuario se quedó sin
     * respuesta. Es la salida que más se usa: casi siempre hay un evento estándar midiendo lo
     * mismo que el evento propio que no aparece.
     *
     * El rótulo es descriptivo a propósito ("lo que registra"), nunca "te recomendamos": un evento
     * con números no es necesariamente la conversión del negocio, y una línea midiendo la
     * conversión equivocada da números lindos y no se queja nunca.
     */
    _renderWhatTheAccountHas() {
        const {t} = this.props;
        const candidates = this._itemsWithDataSorted()
            .filter(item => !this._isSelected(item.id))
            .slice(0, MAX_SUGGESTED_WITH_DATA);
        if (candidates.length === 0) return null;

        return (
            <div className="categorized-actions-has">
                <p className="has-label">{t('categorized_actions.account_registers')}</p>
                <ul className="category-items">
                    {candidates.map(item => this._renderItem(item, null))}
                </ul>
            </div>
        );
    }

    /**
     * Lo que la pantalla contesta cuando no queda nada que mostrar.
     *
     * Verificado el 2026-09-21 en producción: acá antes no había NADA — el listado entero
     * desaparecía y quedaba un rectángulo en blanco, justo en el momento en que el usuario acababa
     * de escribir su pregunta con sus propias palabras.
     */
    _renderEmptyState() {
        const {t} = this.props;
        const isSearching = !!this.state.searchText;
        const info = this._ownEventsInfo();

        // La cuenta no trajo NINGÚN evento. Es alcanzable de verdad: el presenter devuelve la
        // lista pelada y `_sync_actions_from_api` se traga cualquier excepción y devuelve `[]`,
        // así que una propiedad de GA4 sin eventos clave o una cuenta de LinkedIn sin reglas de
        // conversión llegan vacías. Sin esta rama volvía el rectángulo en blanco — el síntoma
        // exacto que originó el frente — y en GA4/LinkedIn, que no tienen línea del pie ni
        // píldoras, quedaba el buscador solo sobre la nada.
        if ((this.props.items || []).length === 0) {
            return (
                <div className="categorized-actions-empty">
                    <p className="empty-title">{t('categorized_actions.own_events_empty_title')}</p>
                    {info && info.howToKey && (
                        <p className="empty-detail">{t(info.howToKey)}</p>
                    )}
                    {info && info.helpArticle && (
                        <LinkToArticle article={info.helpArticle} type={STYLE_WANT_TO_KNOW_MORE}
                                       text={t('categorized_actions.own_events_how_to_link')}
                                       plain={true}/>
                    )}
                </div>
            );
        }

        if (!isSearching) {
            // Vacío por filtro, no por búsqueda: son mensajes distintos y la salida también.
            if (this.state.onlyWithData) {
                return (
                    <div className="categorized-actions-empty">
                        <p className="empty-title">{t('categorized_actions.empty_no_data_title')}</p>
                        <button type="button" className="btn-link-inline"
                                onClick={this.handleToggleOnlyWithData}>
                            {t('categorized_actions.empty_see_all')}
                        </button>
                    </div>
                );
            }
            if (this.state.activeFilterId) {
                return (
                    <div className="categorized-actions-empty">
                        <p className="empty-title">{t('categorized_actions.empty_filter_title')}</p>
                        <button type="button" className="btn-link-inline"
                                onClick={() => this.handleFilterClick(this.state.activeFilterId)}>
                            {t('categorized_actions.empty_clear_filter')}
                        </button>
                    </div>
                );
            }
            return null;
        }

        const term = this.state.searchText;
        const aggregate = this._aggregateItem();
        // Alcanza con que el canal tenga ALGO que decir. Gatearlo sólo por `limitationKey` dejaba
        // el instructivo de GA4 y LinkedIn escrito en los dos bundles y sin un camino que lo
        // dibuje, y a esos usuarios con el genérico «probá con menos palabras».
        const canTeach = !!info && (!!info.limitationKey || !!info.howToKey);

        return (
            <div className="categorized-actions-empty">
                {canTeach ? (
                    <div className="categorized-actions-teach">
                        <p className="teach-title">
                            {t(info.limitationKey
                                ? 'categorized_actions.empty_not_published'
                                : 'categorized_actions.empty_search_title', {
                                term,
                                interpolation: {escapeValue: false},
                            })}
                        </p>
                        {info.limitationKey && <p className="teach-detail">{t(info.limitationKey)}</p>}
                        {info.howToKey && <p className="teach-detail">{t(info.howToKey)}</p>}
                        <div className="teach-actions">
                            {aggregate && (
                                <button type="button" className="btn -alt btn-sm"
                                        onClick={this.handleSelectAggregate}>
                                    {t('categorized_actions.empty_use_aggregate')}
                                </button>
                            )}
                            {info.helpArticle && (
                                <LinkToArticle article={info.helpArticle}
                                               type={STYLE_WANT_TO_KNOW_MORE}
                                               text={t('categorized_actions.own_events_how_to_link')}
                                               plain={true}/>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        <p className="empty-title">
                            {t('categorized_actions.empty_search_title', {
                                term,
                                interpolation: {escapeValue: false},
                            })}
                        </p>
                        <p className="empty-detail">{t('categorized_actions.empty_search_detail')}</p>
                    </>
                )}
                {this._renderWhatTheAccountHas()}
            </div>
        );
    }

    /**
     * El grupo del renglón agregado, al final de la lista.
     *
     * Existe para que la explicación («Meta no los publica uno por uno») esté PEGADA al único
     * renglón que representa esos eventos, en vez de colgada al pie de la pantalla, donde quedaba
     * como un párrafo suelto encima de otro y se leía como un pie de página que nadie asocia con
     * ninguna fila.
     */
    _renderAggregateSection() {
        const {t} = this.props;
        const info = this._ownEventsInfo();
        const items = this._aggregateGroupItems();
        if (!info || !info.aggregateGroupKey || items.length === 0) return null;
        if (this.state.activeFilterId) return null;

        const visibles = items.filter(item =>
            this._matchesSearch(item) && this._itemPassesFilter(item));
        if (visibles.length === 0) return null;

        const isExpanded = !!this.state.searchText || this.state.expandedCategories['aggregate'];
        const selectedCount = this._countSelectedInCategory(items);

        return (
            <div className="categorized-actions-category -aggregate">
                <div className={`category-header ${isExpanded ? '-expanded' : ''}`}
                     onClick={() => this.handleToggleCategory('aggregate')}>
                    <span className="category-arrow">{isExpanded ? '▼' : '▶'}</span>
                    <span className="category-name">{t(info.aggregateGroupKey)}</span>
                    {selectedCount > 0 && (
                        <span className="category-count">
                            ({selectedCount} {t('categorized_actions.selected_singular')})
                        </span>
                    )}
                </div>
                {isExpanded && (
                    <>
                        {this._renderOwnEventsNotice(info)}
                        <ul className="category-items">
                            {visibles.map(item => this._renderItem(item, null))}
                        </ul>
                    </>
                )}
            </div>
        );
    }

    /**
     * El aviso de por qué la plataforma no desglosa los eventos propios y cómo conseguir el tuyo.
     *
     * Es UN bloque que se dibuja en dos grupos: el del renglón agregado («Eventos personalizados del
     * píxel») y el de las conversiones personalizadas, que es donde el usuario va a buscar su
     * evento y donde antes no había una palabra (card ey2MsHC7). En el segundo va con `titleKey`,
     * la pregunta que se hace quien no lo encuentra en la lista.
     */
    _renderOwnEventsNotice(info, titleKey = null) {
        const {t} = this.props;
        return (
            <div className="categorized-actions-teach -inside-category">
                {titleKey && <p className="teach-title">{t(titleKey)}</p>}
                <p className="teach-detail">{t(info.limitationKey)}</p>
                {info.howToKey && <p className="teach-detail">{t(info.howToKey)}</p>}
                {info.helpArticle && (
                    <LinkToArticle article={info.helpArticle}
                                   type={STYLE_WANT_TO_KNOW_MORE}
                                   text={t('categorized_actions.own_events_how_to_link')}
                                   plain={true}/>
                )}
            </div>
        );
    }

    /**
     * La línea del pie, SÓLO en los canales sin categorías (la lista plana).
     *
     * Donde hay grupos, el aviso vive en el grupo del renglón agregado; donde no los hay —Google,
     * GA4, LinkedIn, TikTok— no hay dónde colgarlo y el pie sigue siendo el lugar. Antes estaba en
     * los dos, y en Meta quedaba un párrafo suelto debajo de las categorías.
     */
    _renderChannelFootnote() {
        const {t} = this.props;
        const info = this._ownEventsInfo();
        if (this._shouldCategorize()) return null;
        if (!info || !info.limitationKey) return null;

        return (
            <p className="categorized-actions-footnote">
                {t(info.limitationKey)}
                {info.helpArticle && (
                    <LinkToArticle article={info.helpArticle} type={STYLE_WANT_TO_KNOW_MORE}
                                   text={t('categorized_actions.own_events_how_to_link')}
                                   plain={true}/>
                )}
            </p>
        );
    }

    _renderCategorizedView() {
        const {categorized, custom} = this._getGroupedItems();

        return (
            <div className="categorized-actions-body">
                {CATEGORY_ORDER.map(cat => {
                    if (cat === CATEGORY_POPULAR) {
                        const popularSection = categorized[cat] && categorized[cat].length > 0
                            ? this._renderCategorySection(cat, categorized[cat])
                            : null;
                        const customSection = this._renderCustomSection(custom);
                        if (!popularSection && !customSection) return null;
                        return (
                            <React.Fragment key="popular-and-custom">
                                {popularSection}
                                {customSection}
                            </React.Fragment>
                        );
                    }
                    if (!categorized[cat] || categorized[cat].length === 0) return null;
                    return this._renderCategorySection(cat, categorized[cat]);
                })}
                {/* Último de la lista, a propósito: es el cajón de lo que la plataforma no
                    desglosa, no una categoría del embudo. */}
                {this._renderAggregateSection()}
            </div>
        );
    }

    _renderSimpleList() {
        const items = this._getMappedItems();
        const filteredItems = items.filter(item =>
            this._matchesSearch(item) && this._itemPassesFilter(item));

        // Ordenar: mayor cantidad de eventos (30d) primero, luego alfabético
        filteredItems.sort((a, b) => {
            const countA = (a.raw.counts && a.raw.counts.last_30_days) || 0;
            const countB = (b.raw.counts && b.raw.counts.last_30_days) || 0;
            if (countB !== countA) return countB - countA;
            return a.name.localeCompare(b.name);
        });

        return (
            <div className="categorized-actions-body">
                <ul className="category-items -flat">
                    {filteredItems.map(item => this._renderItem(item, null))}
                </ul>
            </div>
        );
    }

    render() {
        const hasVisibleItems = this._visibleItems().length > 0;

        return (
            <div className="categorized-actions-select">
                {this._renderSearch()}
                {this._renderFilters()}
                {this._renderAllConversionsOption()}
                {this._renderOverlapNotice()}
                {hasVisibleItems
                    ? (this._shouldCategorize()
                        ? this._renderCategorizedView()
                        : this._renderSimpleList())
                    : this._renderEmptyState()}
                {this._renderChannelFootnote()}
            </div>
        );
    }
}

export const CategorizedActionsSelect = withTranslation()(CategorizedActionsSelectClass);
export default CategorizedActionsSelect;
